'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Topbar from '@/components/layout/Topbar';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const offshoreServices = [
{
  id: 'dedicated',
  title: 'Build Dedicated Offshore Teams',
  description: 'Build a fully dedicated team in India that works exclusively for your business. Your offshore team operates as a seamless extension of your in-house team — same culture, same goals, same commitment.',
  features: ['Dedicated Team Model', 'Full-Time Committed Resources', 'Your Brand, Your Culture', 'Direct Communication', 'Transparent Billing', 'Team of 2 to 200+'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_113d56666-1773207076522.png",
  alt: 'Dedicated offshore development team in India working on video conference with global client team'
},
{
  id: 'managed',
  title: 'Managed Development Teams',
  description: 'Let us handle the management while you focus on your business. Our managed development model provides a complete team with project management, QA, and delivery accountability built in.',
  features: ['End-to-End Project Management', 'Agile & Scrum Delivery', 'QA & Testing Included', 'Regular Sprint Reviews', 'Dedicated Project Manager', 'SLA-Backed Delivery'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffbd3757-1784114772577.png",
  alt: 'Managed development team conducting agile sprint review meeting with project manager and developers'
},
{
  id: 'scaling',
  title: 'Team Scaling Solutions',
  description: 'Scale your offshore team up or down based on project demands. Our flexible team scaling model lets you add or reduce resources quickly without the overhead of traditional hiring.',
  features: ['Rapid Team Scaling', 'Flexible Resource Models', 'No Long-Term Lock-In', 'Quick Onboarding (1-2 weeks)', 'Skill-Based Matching', 'Seamless Handover Process'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0db0e9a-1773009704688.png",
  alt: 'Team scaling diagram showing offshore team growth from small to large with flexible resource allocation'
}];


const techServices = [
{
  id: 'technology',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>,

  title: 'Application Development',
  description: 'Custom web and mobile application development using modern tech stacks. From MVPs to enterprise platforms, we build scalable, secure, and high-performance applications powered by your offshore team in India.',
  features: ['Web Application Development', 'Mobile App Development (iOS/Android)', 'API Development & Integration', 'Microservices Architecture', 'Progressive Web Apps (PWA)', 'Legacy System Modernization']
},
{
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>,

  title: 'AI & Machine Learning',
  description: 'Build intelligent AI solutions with our offshore AI/ML teams in India. From predictive models to generative AI applications, NLP, and computer vision — we deliver cutting-edge AI capabilities at a fraction of the cost.',
  features: ['AI/ML Model Development', 'Generative AI & LLM Integration', 'Natural Language Processing (NLP)', 'Computer Vision Solutions', 'MLOps & AI Infrastructure', 'AI-Powered Automation']
},
{
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>,

  title: 'Cloud Services',
  description: 'Migrate, manage, and optimize your cloud infrastructure with dedicated offshore cloud engineers. We help businesses leverage AWS, Azure, and Google Cloud to reduce costs, improve performance, and scale effortlessly.',
  features: ['Cloud Migration Strategy', 'AWS / Azure / GCP Setup', 'Cloud Cost Optimization', 'DevOps & CI/CD Pipelines', 'Cloud Security & Compliance', 'Managed Cloud Services']
},
{
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>,

  title: 'Data & Analytics',
  description: 'Transform raw data into actionable business insights with offshore data teams. Our data engineering and analytics services help you build data pipelines, AI-powered dashboards, and machine learning analytics solutions.',
  features: ['Data Engineering & Pipelines', 'Business Intelligence Dashboards', 'Big Data Solutions', 'Machine Learning Models', 'Data Warehousing', 'Real-Time Analytics']
},
{
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>,

  title: 'Enterprise Solutions',
  description: 'Implement and customize enterprise platforms including ERP, CRM, and Salesforce with cost-effective offshore teams. We help businesses streamline operations and improve efficiency with the right enterprise technology.',
  features: ['Salesforce Implementation', 'ERP Solutions (SAP / Oracle)', 'CRM Development', 'Enterprise Integration', 'Workflow Automation', 'RPA Solutions']
},
{
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>,

  title: 'Digital Marketing Tech',
  description: 'Power your AI digital marketing with the right technology stack. Our offshore tech teams build marketing automation platforms, analytics dashboards, CRM integrations, and AI-powered marketing tools.',
  features: ['Marketing Automation Platforms', 'AI Analytics Dashboards', 'CRM & MarTech Integration', 'SEO & Content Management Systems', 'Ad Tech & Campaign Tools', 'Customer Data Platforms (CDP)']
}];


export default function OffshoreTeamsPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Topbar />
      <Header />
      {/* Page Hero */}
      <section className="page-hero-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-5">
                <span className="text-primary text-sm font-semibold font-lato tracking-wide">PILLAR 3 OF 3</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-poppins leading-tight mb-4">
                Build Offshore Teams
                <span className="block text-primary text-3xl md:text-4xl mt-2">+ Technology Services</span>
              </h1>
              <p className="text-white/70 font-lato text-lg leading-relaxed mb-8">
                Build dedicated global teams in India and leverage our technology services to accelerate your product development and reduce costs by up to 60%.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/contact" className="bbt-btn-primary">Build Your Team</Link>
                <Link href="#technology" className="bbt-btn-outline border-white/40 text-white hover:bg-white hover:text-primary">Technology Services</Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1336d5856-1774283907156.png"
                alt="Global offshore team collaboration with team members from India and USA working together on video call"
                className="rounded-lg shadow-2xl" />
              
            </div>
          </div>
        </div>
      </section>
      {/* Stats */}
      <section className="bbt-stats-section py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
            { value: '50+', label: 'Offshore Teams Built' },
            { value: '60%', label: 'Cost Savings vs Local Hire' },
            { value: '2 Weeks', label: 'Average Team Setup Time' },
            { value: '100%', label: 'Dedicated to Your Business' }]?.
            map((stat, i) =>
            <div key={i}>
                <div className="text-4xl font-bold text-white font-poppins mb-2">{stat?.value}</div>
                <div className="text-white/70 text-sm font-lato">{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Offshore Team Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">OFFSHORE TEAM SOLUTIONS</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">Build, manage, and scale dedicated teams in India for your global business</p>
          </div>
          <div className="space-y-16">
            {offshoreServices?.map((service, i) =>
            <div key={service?.id} id={service?.id} className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center animate-on-scroll">
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <h3 className="text-2xl font-bold text-text-main font-poppins mb-4">{service?.title}</h3>
                  <p className="text-gray-600 font-poppins text-base leading-relaxed mb-6">{service?.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service?.features?.map((f, fi) =>
                  <div key={fi} className="flex items-center gap-2 text-sm text-gray-600 font-lato">
                        <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {f}
                      </div>
                  )}
                  </div>
                  <Link href="/contact" className="bbt-btn-primary text-sm">Get Started</Link>
                </div>
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <img src={service?.image} alt={service?.alt} className="w-full rounded-lg shadow-card-hover" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Technology Services Section */}
      <section id="technology" className="py-20 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-4">
              <span className="text-primary text-sm font-semibold font-lato tracking-wide">SUPPORTING CAPABILITY</span>
            </div>
            <h2 className="bbt-section-title">TECHNOLOGY SERVICES</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle max-w-2xl mx-auto">
              Our technology services power your offshore teams and standalone projects — from application development to AI and cloud infrastructure.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {techServices?.map((service, i) =>
            <div key={i} className="bg-white rounded-lg p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 animate-on-scroll" style={{ animationDelay: `${i % 3 * 0.1}s` }}>
                <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                  {service?.icon}
                </div>
                <h4 className="font-bold text-text-main font-lato text-base mb-3">{service?.title}</h4>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed mb-4">{service?.description}</p>
                <ul className="space-y-1">
                  {service?.features?.slice(0, 3)?.map((f, fi) =>
                <li key={fi} className="flex items-center gap-2 text-xs text-gray-500 font-lato">
                      <svg className="w-3 h-3 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {f}
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Why Offshore */}
      <section className="py-20" style={{ backgroundColor: '#6d97bf' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white font-poppins mb-3">WHY BUILD AN OFFSHORE TEAM WITH BBT?</h2>
            <div className="w-16 h-1 bg-white/50 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
            { icon: '💰', title: 'Up to 60% Cost Savings', desc: 'Reduce your development costs significantly without compromising on quality or talent caliber.' },
            { icon: '⚡', title: 'Fast Team Setup', desc: 'We can have your dedicated offshore team operational within 2 weeks, fully onboarded and ready to deliver.' },
            { icon: '🌍', title: 'Global Talent Access', desc: 'Access India\'s vast pool of 5 million+ IT professionals with expertise across all modern technologies.' },
            { icon: '🔒', title: 'IP Protection', desc: 'Robust NDAs, IP agreements, and security protocols ensure your intellectual property is always protected.' },
            { icon: '📊', title: 'Full Transparency', desc: 'Daily standups, sprint reviews, and real-time project dashboards keep you in full control at all times.' },
            { icon: '🤝', title: 'Cultural Alignment', desc: 'We carefully match team members who align with your company culture, values, and working style.' }]?.
            map((item, i) =>
            <div key={i} className="bg-white/10 border border-white/20 rounded-lg p-6 hover:bg-white/20 transition-all animate-on-scroll" style={{ animationDelay: `${i % 3 * 0.1}s` }}>
                <div className="text-3xl mb-3">{item?.icon}</div>
                <h4 className="font-bold text-white font-lato text-sm uppercase tracking-wide mb-3">{item?.title}</h4>
                <p className="text-white/70 text-sm font-poppins leading-relaxed">{item?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bbt-cta-section rounded-lg">
            <h2 className="text-2xl font-bold text-text-main font-poppins mb-3 uppercase tracking-wide">READY TO BUILD YOUR OFFSHORE TEAM?</h2>
            <p className="text-gray-600 font-poppins mb-8 text-lg">&ldquo;Start with a free consultation and get your team operational in 2 weeks&rdquo;</p>
            <Link href="/contact" className="bbt-btn-primary">BUILD YOUR TEAM</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

}